import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/lib/roles";
import GabineteChip from "@/components/GabineteChip";
import {
  ArrowLeft, Pencil, Check, X, Buildings, IdentificationCard,
  Envelope, MapPin, Receipt, FileArrowDown, Tag, Phone, Image as ImageIcon,
  Trash, UploadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtEUR2 = (n) => {
  const v = Math.trunc((Number(n) || 0) * 100) / 100;
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("es-ES") : "—";

const TODAY = new Date().toISOString().slice(0, 10);
const FIRST_OF_MONTH = TODAY.slice(0, 8) + "01";

export default function GabineteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = can.manageInsurers(user);

  const [gabinete, setGabinete] = useState(null);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState("datos");

  const [desde, setDesde] = useState(FIRST_OF_MONTH);
  const [hasta, setHasta] = useState(TODAY);
  const [factu, setFactu] = useState(null);
  const [loadingFactu, setLoadingFactu] = useState(false);

  const load = async () => {
    const [g, a] = await Promise.all([
      api.get(`/gabinetes/${id}`),
      api.get("/aseguradoras"),
    ]);
    setGabinete(g.data);
    setForm(g.data);
    setAseguradoras((a.data || []).filter((x) => x.active !== false));
  };
  useEffect(() => { load(); }, [id]);

  const aseguradorasMap = useMemo(() => {
    const m = new Map();
    for (const a of aseguradoras) m.set(a.id, a);
    return m;
  }, [aseguradoras]);

  const save = async () => {
    await api.put(`/gabinetes/${id}`, {
      name: form.name,
      cif: form.cif || null,
      direccion_fiscal: form.direccion_fiscal || null,
      email: form.email || null,
      telefono: form.telefono || null,
      aseguradoras_ids: form.aseguradoras_ids || [],
      active: form.active,
      es_cliente: !!form.es_cliente,
      notas: form.notas || null,
      short_label: (form.short_label || "").trim() || null,
      color: form.color || null,
    });
    setEditing(false);
    load();
  };

  const cargarFacturacion = async () => {
    setLoadingFactu(true);
    try {
      const r = await api.get(`/gabinetes/${id}/facturacion`, {
        params: { desde, hasta },
      });
      setFactu(r.data);
    } finally {
      setLoadingFactu(false);
    }
  };

  useEffect(() => {
    if (tab === "facturacion") cargarFacturacion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const toggleAseg = (aid) => {
    const has = form.aseguradoras_ids.includes(aid);
    setForm({
      ...form,
      aseguradoras_ids: has
        ? form.aseguradoras_ids.filter((x) => x !== aid)
        : [...form.aseguradoras_ids, aid],
    });
  };

  const exportCSV = () => {
    if (!factu) return;
    const rows = [
      ["Siniestro", "Póliza", "Asegurado", "Aseguradora", "Cierre", "Tasación", "Minuta"],
      ...factu.expedientes.map((e) => [
        e?.extracted_data?.numero_siniestro || "",
        e?.extracted_data?.numero_poliza || "",
        e?.extracted_data?.asegurado_nombre || "",
        e?.detected_aseguradora || e?.extracted_data?.aseguradora || "",
        e.fecha_cierre || "",
        (e.importe_tasacion ?? "").toString().replace(".", ","),
        (e.importe_minuta ?? "").toString().replace(".", ","),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `facturacion_${gabinete.name.replace(/\s+/g, "_")}_${desde}_${hasta}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!gabinete) return null;

  return (
    <div data-testid="gabinete-detail-page">
      <header className="h-14 border-b border-border bg-white px-6 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate("/admin/gabinetes")}
            className="text-muted hover:text-foreground inline-flex items-center gap-1 text-xs"
          >
            <ArrowLeft className="w-3.5 h-3.5" weight="bold" /> Gabinetes
          </button>
          <div className="text-muted">·</div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted">Gabinete</div>
            <div className="flex items-center gap-2 min-w-0">
              <h1 className="font-display font-bold text-lg tracking-tight truncate">{gabinete.name}</h1>
              <GabineteChip gabinete={gabinete} size="md" />
            </div>
          </div>
        </div>
        {canEdit && !editing && tab === "datos" && (
          <button
            onClick={() => setEditing(true)}
            data-testid="edit-gab"
            className="text-xs border border-border hover:border-foreground rounded-md px-3 py-1.5 inline-flex items-center gap-1.5"
          >
            <Pencil className="w-3 h-3" weight="bold" />Editar
          </button>
        )}
      </header>

      <div className="border-b border-border bg-white px-6 flex gap-1">
        {[
          { id: "datos", label: "Datos" },
          { id: "facturacion", label: "Facturación", icon: Receipt },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`gab-tab-${t.id}`}
            className={`px-3 py-2 text-xs font-semibold border-b-2 transition-colors inline-flex items-center gap-1.5 ${
              tab === t.id
                ? "border-foreground text-foreground"
                : "border-transparent text-muted hover:text-foreground"
            }`}
          >
            {t.icon && <t.icon className="w-3.5 h-3.5" weight="bold" />}
            {t.label}
          </button>
        ))}
      </div>

      {tab === "datos" && (
        <div className="p-6 max-w-3xl space-y-5">
          {!editing ? (
            <>
              <FieldShow icon={IdentificationCard} label="CIF" value={gabinete.cif} mono />
              <FieldShow icon={Envelope} label="Email" value={gabinete.email} />
              <FieldShow icon={Phone} label="Teléfono" value={gabinete.telefono} />
              <FieldShow icon={MapPin} label="Dirección fiscal" value={gabinete.direccion_fiscal} />
              <LogoGabineteBlock gabinete={gabinete} onChange={load} canEdit={canEdit} />
              {/* Etiqueta corta (chip de color) visible en Bandeja, detalle y móvil */}
              <div className="flex items-start gap-3" data-testid="gab-label-row">
                <div className="w-32 flex-shrink-0 text-[11px] uppercase tracking-widest font-semibold text-muted inline-flex items-center gap-1.5">
                  <Tag className="w-3 h-3" weight="bold" />
                  Etiqueta corta
                </div>
                <div className="flex-1 min-w-0">
                  {gabinete.short_label ? (
                    <div className="inline-flex items-center gap-2">
                      <GabineteChip gabinete={gabinete} size="md" />
                      <span className="text-[11px] text-muted">
                        Se muestra junto al nombre de la aseguradora en Bandeja, en el detalle del expediente y en la app móvil.
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted italic">
                      Sin etiqueta. Edita para añadir una (máx 8 caracteres) y elige un color.
                    </span>
                  )}
                </div>
              </div>
              {gabinete.es_cliente && (
                <div className="flex items-center justify-between bg-amber-50/60 border border-amber-200 rounded-md px-3 py-2">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Es cliente</span>
                    <p className="text-xs text-foreground mt-0.5">Este gabinete tiene ficha en Clientes de facturación.</p>
                  </div>
                  {gabinete.cliente_facturacion_id && (
                    <button
                      onClick={() => navigate(`/admin/clientes-facturacion?focus=${gabinete.cliente_facturacion_id}`)}
                      className="text-xs px-3 py-1.5 rounded-md bg-foreground text-white font-semibold hover:opacity-90"
                      data-testid="gab-go-cliente"
                    >Ver ficha</button>
                  )}
                </div>
              )}
              <div>
                <div className="text-[11px] uppercase tracking-widest font-semibold text-muted mb-2 inline-flex items-center gap-1">
                  <Buildings className="w-3 h-3" weight="bold" />
                  Aseguradoras vinculadas ({(gabinete.aseguradoras_ids || []).length})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(gabinete.aseguradoras_ids || []).map((aid) => {
                    const a = aseguradorasMap.get(aid);
                    if (!a) return null;
                    return (
                      <button
                        key={aid}
                        onClick={() => navigate(`/admin/aseguradoras/${aid}`)}
                        className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 px-2 py-1 rounded inline-flex items-center gap-1.5 transition-colors"
                      >
                        <span className="font-display font-semibold">{a.name}</span>
                        <span className="font-mono text-[10px] text-muted">{a.code}</span>
                      </button>
                    );
                  })}
                  {(gabinete.aseguradoras_ids || []).length === 0 && (
                    <span className="text-xs text-amber-700 italic">Sin aseguradoras vinculadas</span>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <Field label="Nombre completo">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="CIF">
                  <input value={form.cif || ""} onChange={(e) => setForm({ ...form, cif: e.target.value.toUpperCase() })} className="w-full border border-border rounded-md px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground" />
                </Field>
                <Field label="Email">
                  <input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Teléfono">
                  <input type="tel" value={form.telefono || ""} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="gab-telefono" className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground" />
                </Field>
                <div />
              </div>
              <Field label="Dirección fiscal">
                <input value={form.direccion_fiscal || ""} onChange={(e) => setForm({ ...form, direccion_fiscal: e.target.value })} className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground" />
              </Field>
              {/* Etiqueta corta + color (chip visible en Bandeja y detalle) */}
              <Field label="Etiqueta corta para Bandeja">
                <div className="grid grid-cols-12 gap-3 items-start">
                  <div className="col-span-12 md:col-span-6">
                    <input
                      value={form.short_label || ""}
                      maxLength={8}
                      onChange={(e) => setForm({ ...form, short_label: e.target.value.slice(0, 8) })}
                      placeholder="Ej. GEFOEX, GAB, MAD…"
                      data-testid="gab-short-label-input"
                      className="w-full border border-border rounded-md px-3 py-2 text-sm uppercase font-bold tracking-wide focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
                    />
                    <p className="text-[10.5px] text-muted mt-1">
                      Máx 8 caracteres. Se muestra junto al nombre de la aseguradora en Bandeja y en la app móvil.
                    </p>
                  </div>
                  <div className="col-span-12 md:col-span-6">
                    <div className="text-[10.5px] uppercase tracking-widest font-semibold text-muted mb-1.5">Color del chip</div>
                    <ColorPicker
                      value={form.color}
                      onChange={(v) => setForm({ ...form, color: v })}
                    />
                  </div>
                  {/* Preview en vivo */}
                  <div className="col-span-12 flex items-center gap-2 mt-1">
                    <span className="text-[11px] text-muted">Vista previa:</span>
                    <GabineteChip
                      gabinete={{ id: "preview", short_label: form.short_label, color: form.color, name: form.name }}
                      size="md"
                    />
                    {!form.short_label && <span className="text-[11px] text-muted italic">— sin etiqueta —</span>}
                  </div>
                </div>
              </Field>
              <Field label={`Aseguradoras vinculadas (${form.aseguradoras_ids.length})`}>
                <div className="border border-border rounded-md max-h-56 overflow-y-auto bg-white">
                  {aseguradoras.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 px-3 py-1.5 text-sm border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={form.aseguradoras_ids.includes(a.id)} onChange={() => toggleAseg(a.id)} className="rounded border-border" />
                      <span className="flex-1">{a.name}</span>
                      <span className="font-mono text-[11px] text-muted">{a.code}</span>
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Estado">
                <label className="text-sm inline-flex items-center gap-2">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="rounded border-border" />
                  Activo
                </label>
              </Field>
              <Field label="Facturación">
                <label className="text-sm inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.es_cliente}
                    onChange={(e) => setForm({ ...form, es_cliente: e.target.checked })}
                    className="rounded border-border"
                    data-testid="gab-es-cliente"
                  />
                  Es cliente
                </label>
                <p className="text-[11px] text-muted mt-1">Si lo marcas, se creará automáticamente una ficha en <strong>Clientes de facturación</strong> con los datos de este gabinete para poder emitirle facturas.</p>
              </Field>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => { setForm(gabinete); setEditing(false); }} className="text-xs px-4 py-2 text-muted hover:text-foreground inline-flex items-center gap-1">
                  <X className="w-3 h-3" weight="bold" />Cancelar
                </button>
                <button onClick={save} data-testid="save-edit-gab" className="bg-foreground text-white text-xs px-4 py-2 rounded-md font-medium inline-flex items-center gap-1">
                  <Check className="w-3 h-3" weight="bold" />Guardar cambios
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "facturacion" && (
        <div className="p-6 max-w-6xl space-y-5">
          <div className="flex items-end gap-3 flex-wrap bg-white border border-border rounded-md p-4">
            <Field label="Desde">
              <input
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                data-testid="factu-desde"
                className="border border-border rounded-md px-3 py-1.5 text-sm bg-white"
              />
            </Field>
            <Field label="Hasta">
              <input
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                data-testid="factu-hasta"
                className="border border-border rounded-md px-3 py-1.5 text-sm bg-white"
              />
            </Field>
            <button
              onClick={cargarFacturacion}
              disabled={loadingFactu}
              data-testid="factu-recalc"
              className="bg-foreground text-white text-xs px-4 py-2 rounded-md font-medium disabled:opacity-50"
            >
              {loadingFactu ? "Calculando…" : "Actualizar"}
            </button>
            {factu && (
              <button
                onClick={exportCSV}
                data-testid="factu-export-csv"
                className="border border-border hover:border-foreground rounded-md text-xs px-3 py-2 inline-flex items-center gap-1.5"
              >
                <FileArrowDown className="w-3 h-3" weight="bold" /> Exportar CSV
              </button>
            )}
          </div>

          {factu && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Stat label="Expedientes" value={factu.totales.expedientes} testid="factu-stat-total" />
                <Stat label="Cerrados" value={factu.totales.cerrados} testid="factu-stat-cerrados" />
                <Stat
                  label="Importe minuta total"
                  value={fmtEUR2(factu.totales.importe_minuta)}
                  highlight
                  testid="factu-stat-minuta"
                />
                <Stat label="Importe tasación total" value={fmtEUR2(factu.totales.importe_tasacion)} testid="factu-stat-tasacion" />
              </div>

              <div className="bg-white border border-border rounded-md overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 font-display font-bold text-sm tracking-tight">
                  Expedientes en el rango ({factu.expedientes.length})
                </div>
                {factu.expedientes.length === 0 ? (
                  <div className="text-center py-12 text-muted text-sm">Sin expedientes en este rango.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="thead-modern">
                      <tr>
                        <th className="text-left px-4 py-2">Siniestro</th>
                        <th className="text-left px-4 py-2">Aseguradora</th>
                        <th className="text-left px-4 py-2">Asegurado</th>
                        <th className="text-left px-4 py-2">Cierre</th>
                        <th className="text-right px-4 py-2">Tasación</th>
                        <th className="text-right px-4 py-2">Minuta</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {factu.expedientes.map((e) => (
                        <tr
                          key={e.id}
                          className="hover:bg-slate-50 cursor-pointer"
                          onClick={() => navigate(`/expedientes/${e.id}`)}
                          data-testid={`factu-row-${e.id}`}
                        >
                          <td className="px-4 py-2 font-mono text-xs">{e?.extracted_data?.numero_siniestro || "—"}</td>
                          <td className="px-4 py-2 text-xs">{e?.detected_aseguradora || e?.extracted_data?.aseguradora || "—"}</td>
                          <td className="px-4 py-2 text-xs truncate max-w-[200px]">{e?.extracted_data?.asegurado_nombre || "—"}</td>
                          <td className="px-4 py-2 text-xs">{fmtDate(e.fecha_cierre)}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-xs">{fmtEUR2(e.importe_tasacion)}</td>
                          <td className="px-4 py-2 text-right tabular-nums font-semibold text-xs">{fmtEUR2(e.importe_minuta)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50 font-semibold">
                      <tr>
                        <td colSpan={4} className="px-4 py-2 text-xs">Total</td>
                        <td className="px-4 py-2 text-right tabular-nums text-xs">{fmtEUR2(factu.totales.importe_tasacion)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-xs text-emerald-700">{fmtEUR2(factu.totales.importe_minuta)}</td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-widest font-semibold text-muted mb-1">{label}</label>
      {children}
    </div>
  );
}

function FieldShow({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-baseline gap-3">
      <div className="text-[11px] uppercase tracking-widest font-semibold text-muted w-40 inline-flex items-center gap-1.5 flex-shrink-0">
        {Icon && <Icon className="w-3 h-3" weight="bold" />}{label}
      </div>
      <div className={`text-sm flex-1 ${mono ? "font-mono" : ""} ${!value ? "text-muted italic" : ""}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight, testid }) {
  return (
    <div
      data-testid={testid}
      className={`bg-white border rounded-md p-4 ${
        highlight ? "border-emerald-300 ring-1 ring-emerald-200 bg-emerald-50/30" : "border-border"
      }`}
    >
      <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">{label}</div>
      <div className={`mt-1 font-display font-bold tracking-tight tabular-nums ${highlight ? "text-emerald-700 text-2xl" : "text-foreground text-xl"}`}>
        {value}
      </div>
    </div>
  );
}


// Paleta sugerida: 10 colores con buen contraste para los chips. El usuario
// puede pegar también un HEX libre si necesita un tono concreto.
const COLOR_PRESETS = [
  "#1E88E5", // azul
  "#43A047", // verde
  "#F4511E", // naranja
  "#8E24AA", // morado
  "#E53935", // rojo
  "#FB8C00", // ámbar
  "#00897B", // teal
  "#5E35B1", // índigo
  "#6D4C41", // marrón
  "#546E7A", // gris-azulado
];

function ColorPicker({ value, onChange }) {
  const isCustom = !!value && !COLOR_PRESETS.includes(value);
  return (
    <div data-testid="gab-color-picker">
      <div className="flex flex-wrap gap-1.5">
        {COLOR_PRESETS.map((c) => {
          const active = (value || "").toLowerCase() === c.toLowerCase();
          return (
            <button
              key={c}
              type="button"
              onClick={() => onChange(c)}
              data-testid={`gab-color-${c.replace("#", "").toLowerCase()}`}
              title={c}
              className={`w-7 h-7 rounded-md border-2 transition-all ${
                active ? "border-foreground ring-2 ring-foreground/20 scale-110" : "border-white shadow-sm hover:scale-105"
              }`}
              style={{ backgroundColor: c }}
            />
          );
        })}
        <button
          type="button"
          onClick={() => onChange(null)}
          title="Sin color (gris neutro)"
          className={`w-7 h-7 rounded-md border-2 transition-all bg-zinc-200 ${
            !value ? "border-foreground ring-2 ring-foreground/20 scale-110" : "border-white shadow-sm hover:scale-105"
          }`}
          data-testid="gab-color-none"
        >
          <X className="w-3 h-3 mx-auto text-zinc-500" weight="bold" />
        </button>
      </div>
      {/* Input HEX libre para casos donde el preset no encaja */}
      <div className="mt-2 flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest text-muted font-semibold">HEX</span>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => {
            const v = e.target.value.trim();
            onChange(v || null);
          }}
          placeholder="#RRGGBB"
          maxLength={7}
          data-testid="gab-color-hex"
          className={`w-28 border border-border rounded-md px-2 py-1 text-[11px] font-mono uppercase focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground ${
            isCustom ? "border-foreground" : ""
          }`}
        />
      </div>
    </div>
  );
}


/**
 * Bloque de gestión del logo del gabinete. Se muestra en modo vista
 * (dentro del tab «Datos» del gabinete): preview + botones subir /
 * reemplazar / eliminar. Al guardar se dispara `onChange` para
 * refrescar el gabinete padre.
 */
function LogoGabineteBlock({ gabinete, onChange, canEdit }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("El logo no puede pesar más de 4 MB");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/gabinetes/${gabinete.id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo actualizado");
      onChange?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo subir el logo";
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const eliminar = async () => {
    if (!window.confirm(`¿Eliminar el logo de "${gabinete.name}"?`)) return;
    try {
      await api.delete(`/gabinetes/${gabinete.id}/logo`);
      toast.success("Logo eliminado");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const toggleColegio = async (checked) => {
    try {
      await api.put(`/gabinetes/${gabinete.id}`, {
        mostrar_logo_colegio: checked,
      });
      toast.success(
        checked
          ? "El informe mostrará el logo del colegio"
          : "El informe NO mostrará el logo del colegio",
      );
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const hasLogo = !!gabinete.logo_path;
  // Añadimos un query param con `logo_path` para forzar el reload de
  // la imagen cuando se sustituye (evita caché del navegador).
  const src = hasLogo
    ? `${process.env.REACT_APP_BACKEND_URL}/api/gabinetes/${gabinete.id}/logo?v=${encodeURIComponent(gabinete.logo_path)}`
    : null;

  return (
    <div className="flex items-start gap-3" data-testid="gab-logo-row">
      <div className="w-32 flex-shrink-0 text-[11px] uppercase tracking-widest font-semibold text-muted inline-flex items-center gap-1.5">
        <ImageIcon className="w-3 h-3" weight="bold" />
        Logo
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-4">
          <div
            className="w-24 h-24 border border-border rounded-md flex items-center justify-center bg-white overflow-hidden"
            data-testid="gab-logo-preview"
          >
            {hasLogo ? (
              <img
                src={src}
                alt={`Logo ${gabinete.name}`}
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <div className="text-[10px] text-muted text-center px-2">
                Sin logo
                <br />
                <span className="text-[9px]">Se usará el logo por defecto</span>
              </div>
            )}
          </div>
          {canEdit && (
            <div className="flex flex-col gap-2">
              <label
                className={`inline-flex items-center gap-2 text-xs font-semibold rounded-md px-3 py-1.5 border cursor-pointer ${
                  uploading
                    ? "bg-slate-100 text-slate-400 border-slate-200 cursor-wait"
                    : "bg-foreground text-white border-foreground hover:opacity-90"
                }`}
                data-testid="gab-logo-upload-btn"
              >
                <UploadSimple className="w-3.5 h-3.5" weight="bold" />
                {hasLogo ? "Reemplazar logo" : "Subir logo"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => upload(e.target.files?.[0])}
                  data-testid="gab-logo-file-input"
                />
              </label>
              {hasLogo && (
                <button
                  type="button"
                  onClick={eliminar}
                  data-testid="gab-logo-delete-btn"
                  className="inline-flex items-center gap-2 text-xs font-semibold rounded-md px-3 py-1.5 border bg-white text-status-error-fg border-red-200 hover:bg-red-50"
                >
                  <Trash className="w-3.5 h-3.5" weight="bold" />
                  Eliminar logo
                </button>
              )}
            </div>
          )}
        </div>
        <p className="mt-2 text-[11px] text-muted">
          Se usará en la cabecera y firma del PDF del informe cuando el
          expediente esté asignado a este gabinete. Formatos: PNG, JPG,
          WEBP, SVG (máx 4 MB). Si no hay logo, el informe usa el logo
          por defecto de tu gabinete propio.
        </p>
        {error && <p className="mt-1 text-[11px] text-status-error-fg">{error}</p>}

        {/* Toggle: incluir o no el logo secundario (colegio/APCAS) */}
        <label
          className="mt-3 inline-flex items-start gap-2 cursor-pointer select-none"
          data-testid="gab-mostrar-colegio-row"
        >
          <input
            type="checkbox"
            checked={gabinete.mostrar_logo_colegio !== false}
            disabled={!canEdit}
            onChange={(e) => toggleColegio(e.target.checked)}
            data-testid="gab-mostrar-colegio-input"
            className="mt-0.5 w-4 h-4"
          />
          <span className="text-[12px] text-foreground">
            Incluir el logo del colegio / asociación (APCAS) en el informe
            <span className="block text-[10.5px] text-muted mt-0.5">
              Desmárcalo si el gabinete no quiere el logo del colegio en la
              cabecera y portada del PDF.
            </span>
          </span>
        </label>
      </div>
    </div>
  );
}
